import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Avatar,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getUpcomingEvents,
  getEventDetail,
  getEventGroups,
  createEvent,
  updateEvent,
  deleteEvent,
} from '../../../endpoints/eventsTs';
import {
  EventDetailResponse,
  CreateEventRequest,
  UpcomingEventResponse,
  HostRequest,
} from '../../../types/events';
import { eventType, groupTypes } from '../../../utils/variables';
import UserSearchModal from '../../../Components/UserSearchModal';
import StateSelector from '../../../Components/StateSelector';
import CountrySelector from '../../../Components/CountrySelector';
import { getCountries, getStates } from '../../../endpoints/lookups';

// Import icon images
import BookGroupIcon from '../../../assets/BookGroupIcon.png';
import WineGroupIcon from '../../../assets/WineGroupIcon.png';
import WalkingRunningGroupIcon from '../../../assets/WalkingRunningGroupIcon.png';
import DinnerGroupIcon from '../../../assets/DinnerGroupIcon.png';
import CoffeeGroupIcon from '../../../assets/CoffeeGroupIcon.png';
import CraftsGroupIcon from '../../../assets/CraftsGroupIcon.png';
import GameGroupIcon from '../../../assets/GameGroupIcon.png';
import SocialMeetingIconGold from '../../../assets/SocialMeetingIconGold.png';
import LocalChapterMeetingIcon from '../../../assets/LocalChapterMeetingIcon.png';
import DefaultIcon from '../../../assets/DefaultIcon.png';

dayjs.extend(utc);

interface LCEventsProps {
  id: string;
  lcAccess: boolean;
  canEditEvents: boolean;
}

const LCEvents: React.FC<LCEventsProps> = ({ id, lcAccess, canEditEvents }) => {
  const [startDate, setStartDate] = useState<Dayjs>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs>(dayjs().add(90, 'days'));
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [eventDetail, setEventDetail] = useState<EventDetailResponse | null>(
    null
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<CreateEventRequest>>({});
  const [createData, setCreateData] = useState<Partial<CreateEventRequest>>({
    title: '',
    description: '',
    meeting_on: '',
    ending_on: '',
    meeting_link: '',
    event_type_id: '',
    local_chapter_id: id,
    group_id: '',
    location_name: '',
    location_user_id: '',
    meeting_address: {},
    hosts: [],
  });

  // Location type: 'member' or 'business'
  const [createLocationType, setCreateLocationType] = useState<
    'member' | 'business'
  >('member');
  const [editLocationType, setEditLocationType] = useState<
    'member' | 'business'
  >('member');

  // Selected country/state objects for selectors
  const [createSelectedCountry, setCreateSelectedCountry] = useState<any>(null);
  const [editSelectedCountry, setEditSelectedCountry] = useState<any>(null);
  const [editSelectedState, setEditSelectedState] = useState<any>(null);

  // User search modals
  const [showLocationUserModal, setShowLocationUserModal] = useState(false);
  const [showHostUserModal, setShowHostUserModal] = useState(false);
  const [isCreateModal, setIsCreateModal] = useState(true); // true for create, false for edit
  const [selectedHostIndex, setSelectedHostIndex] = useState<number>(-1);

  // Store host names for display (host ID -> name)
  const [createHostNames, setCreateHostNames] = useState<Map<string, string>>(
    new Map()
  );
  const [editHostNames, setEditHostNames] = useState<Map<string, string>>(
    new Map()
  );

  // Fetch countries and states for lookup
  const { data: countries } = useApiGet(['donation-countries'], getCountries, {
    enabled: true,
  });

  const countryIdForStates =
    createSelectedCountry?.id ||
    editSelectedCountry?.id ||
    createData.meeting_address?.country_id ||
    editData.meeting_address?.country_id;
  const { data: states } = useApiGet(
    ['donation-states', countryIdForStates],
    () => getStates({ countryId: countryIdForStates }),
    {
      enabled: !!countryIdForStates,
    }
  );

  // Format dates for API call
  const startDateStr = useMemo(
    () => startDate.format('YYYY-MM-DD'),
    [startDate]
  );
  const endDateStr = useMemo(() => endDate.format('YYYY-MM-DD'), [endDate]);

  // Get timezone offset in hours (JavaScript returns minutes, inverted sign)
  // Convert to standard format: UTC-5 (EST) = -5, UTC+5 = +5
  const timezoneOffset = useMemo(() => {
    return -new Date().getTimezoneOffset() / 60;
  }, []);

  // Fetch events
  const {
    data: eventsData,
    isFetching,
    refetch,
  } = useApiGet(
    ['lc-events', id, startDateStr, endDateStr, timezoneOffset],
    () =>
      getUpcomingEvents(
        startDateStr,
        endDateStr,
        id,
        undefined,
        timezoneOffset
      ),
    {
      enabled: !!id,
      retry: 1,
    }
  );

  // Fetch event groups
  const { data: eventGroups } = useApiGet(
    ['event-groups'],
    () => getEventGroups(),
    {
      enabled: true,
      retry: 1,
    }
  );

  // Fetch event detail when modal opens
  const { data: detailData, refetch: refetchDetail } = useApiGet(
    ['event-detail', selectedEvent],
    () => getEventDetail(selectedEvent!),
    {
      enabled: !!selectedEvent && showDetailModal,
      retry: 1,
    }
  );

  // Events are already filtered by the backend based on start_date and end_date
  const filteredEvents = eventsData || [];

  // Update event detail when data loads
  useEffect(() => {
    if (detailData) {
      setEventDetail(detailData);
    }
  }, [detailData]);

  // Update selected country/state objects when countries/states load and we have IDs
  useEffect(() => {
    if (
      showEditModal &&
      editData.meeting_address?.country_id &&
      countries &&
      !editSelectedCountry
    ) {
      const foundCountry = countries.find(
        (c: any) => c.id === editData.meeting_address?.country_id
      );
      if (foundCountry) {
        setEditSelectedCountry(foundCountry);
      }
    }
  }, [
    countries,
    showEditModal,
    editData.meeting_address?.country_id,
    editSelectedCountry,
  ]);

  useEffect(() => {
    if (
      showEditModal &&
      editData.meeting_address?.state_id &&
      states &&
      !editSelectedState
    ) {
      const foundState = states.find(
        (s: any) => s.id === editData.meeting_address?.state_id
      );
      if (foundState) {
        setEditSelectedState(foundState);
      }
    }
  }, [
    states,
    showEditModal,
    editData.meeting_address?.state_id,
    editSelectedState,
  ]);

  const handleEventClick = (eventId: string) => {
    setSelectedEvent(eventId);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedEvent(null);
    setEventDetail(null);
  };

  const handleCreateEvent = () => {
    setShowCreateModal(true);
    setCreateLocationType('member');
    setCreateData({
      title: '',
      description: '',
      meeting_on: '',
      ending_on: '',
      meeting_link: '',
      event_type_id: '',
      local_chapter_id: id,
      group_id: '',
      location_name: '',
      location_user_id: '',
      meeting_address: {},
      hosts: [],
    });
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateError(null);
    setCreateLocationType('member');
    setCreateSelectedCountry(null);
    setCreateHostNames(new Map());
    setCreateData({
      title: '',
      description: '',
      meeting_on: '',
      ending_on: '',
      meeting_link: '',
      event_type_id: '',
      local_chapter_id: id,
      group_id: '',
      location_name: '',
      location_user_id: '',
      meeting_address: {},
      hosts: [],
    });
  };

  const { mutate: createEventMutation } = useApiSend(
    (data: CreateEventRequest) => createEvent(data),
    () => {
      setIsSaving(false);
      setShowCreateModal(false);
      refetch();
    },
    (error: Error) => {
      setIsSaving(false);
      console.error('Failed to create event:', error);
    }
  );

  const { mutate: updateEventMutation } = useApiSend(
    ({
      eventId,
      updateData,
    }: {
      eventId: string;
      updateData: Partial<CreateEventRequest>;
    }) => updateEvent(eventId, updateData),
    () => {
      setIsSaving(false);
      setShowEditModal(false);
      // Don't close detail modal, just refetch the detail
      if (selectedEvent) {
        refetchDetail();
      }
      refetch();
    },
    (error: Error) => {
      setIsSaving(false);
      console.error('Failed to update event:', error);
    },
    [
      ['event-detail', selectedEvent],
      ['lc-events', id, startDateStr, endDateStr],
    ]
  );

  const { mutate: deleteEventMutation } = useApiSend(
    (eventId: string) => deleteEvent(eventId),
    () => {
      setShowDeleteConfirm(false);
      setShowDetailModal(false);
      setSelectedEvent(null);
      setEventDetail(null);
      refetch();
    },
    (error: Error) => {
      console.error('Failed to delete event:', error);
    },
    [
      ['event-detail', selectedEvent],
      ['lc-events', id, startDateStr, endDateStr],
    ]
  );

  // Convert local datetime to UTC ISO format for API
  const convertToUTCISO = (dateTimeString: string): string => {
    if (!dateTimeString) return dateTimeString;
    // Parse as local time and convert to UTC ISO
    return dayjs(dateTimeString).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
  };

  const handleSaveEvent = () => {
    // Clear previous errors
    setCreateError(null);

    // Validate required fields
    if (
      !createData.title ||
      !createData.meeting_on ||
      !createData.event_type_id
    ) {
      setCreateError('Please fill in all required fields.');
      return;
    }

    // Validate end date is required
    if (!createData.ending_on) {
      setCreateError('Ending date/time is required.');
      return;
    }

    // Validate end date is not before start date
    if (dayjs(createData.ending_on).isBefore(dayjs(createData.meeting_on))) {
      setCreateError(
        'Ending date/time cannot be before the meeting date/time.'
      );
      return;
    }

    setIsSaving(true);
    const cleanedData = prepareEventData(createData, createLocationType);
    // Convert dates to UTC ISO format
    if (cleanedData.meeting_on) {
      cleanedData.meeting_on = convertToUTCISO(cleanedData.meeting_on);
    }
    if (cleanedData.ending_on) {
      cleanedData.ending_on = convertToUTCISO(cleanedData.ending_on);
    }
    createEventMutation(cleanedData as CreateEventRequest);
  };

  const handleEditEvent = () => {
    if (!eventDetail) return;

    // Determine location type
    const locationType = eventDetail.location_user_id ? 'member' : 'business';
    setEditLocationType(locationType);

    // Convert hosts from response format to request format
    const hosts: HostRequest[] =
      eventDetail.hosts?.map((host) => ({
        id: host.id,
        is_primary: host.is_primary,
      })) || [];

    // Store host names for display
    const hostNamesMap = new Map<string, string>();
    eventDetail.hosts?.forEach((host) => {
      hostNamesMap.set(host.id, host.name);
    });
    setEditHostNames(hostNamesMap);

    // Reset country/state selections - will be populated by useEffect when data loads
    setEditSelectedCountry(null);
    setEditSelectedState(null);

    // Convert UTC dates from API to local time for display
    // API sends dates in format: "YYYY-MM-DD HH:mm:ss" (UTC)
    const convertUTCToLocal = (
      utcDateTimeString: string | null | undefined
    ): string => {
      if (!utcDateTimeString) return '';
      // Parse UTC string and convert to local time
      // The format from API is "YYYY-MM-DD HH:mm:ss" which is UTC
      return dayjs.utc(utcDateTimeString).local().format('YYYY-MM-DD HH:mm:ss');
    };

    setEditData({
      title: eventDetail.title,
      description: eventDetail.description || '',
      meeting_on: convertUTCToLocal(eventDetail.meeting_on),
      ending_on: convertUTCToLocal(eventDetail.ending_on),
      meeting_link: eventDetail.meeting_link || '',
      event_type_id: eventDetail.event_type_id,
      local_chapter_id: id,
      group_id: eventDetail.group_id || '',
      location_name: eventDetail.location_name || '',
      location_user_id: eventDetail.location_user_id || '',
      meeting_address: eventDetail.meeting_address
        ? {
            address_line_1: eventDetail.meeting_address.address_line_1 || '',
            address_line_2: eventDetail.meeting_address.address_line_2 || '',
            city: eventDetail.meeting_address.city || '',
            state_id: eventDetail.meeting_address.state_id || '',
            postal_code: eventDetail.meeting_address.postal_code || '',
            country_id: eventDetail.meeting_address.country_id || '',
          }
        : {},
      hosts: hosts,
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditError(null);
    setEditLocationType('member');
    setEditSelectedCountry(null);
    setEditSelectedState(null);
    setEditHostNames(new Map());
    setEditData({});
  };

  const handleSaveEditEvent = () => {
    if (!selectedEvent) return;

    // Clear previous errors
    setEditError(null);

    // Validate required fields
    if (!editData.title || !editData.meeting_on || !editData.event_type_id) {
      setEditError('Please fill in all required fields.');
      return;
    }

    // Validate end date is required
    if (!editData.ending_on) {
      setEditError('Ending date/time is required.');
      return;
    }

    // Validate end date is not before start date
    if (dayjs(editData.ending_on).isBefore(dayjs(editData.meeting_on))) {
      setEditError('Ending date/time cannot be before the meeting date/time.');
      return;
    }

    setIsSaving(true);
    const cleanedData = prepareEventData(editData, editLocationType);
    // Convert dates to UTC ISO format
    if (cleanedData.meeting_on) {
      cleanedData.meeting_on = convertToUTCISO(cleanedData.meeting_on);
    }
    if (cleanedData.ending_on) {
      cleanedData.ending_on = convertToUTCISO(cleanedData.ending_on);
    }
    updateEventMutation({ eventId: selectedEvent, updateData: cleanedData });
  };

  const handleDeleteEvent = () => {
    if (!selectedEvent) return;
    deleteEventMutation(selectedEvent);
  };

  const isEventInFuture = (event: EventDetailResponse | null): boolean => {
    if (!event) return false;
    return dayjs(event.meeting_on).isAfter(dayjs());
  };

  // Location type change handlers
  const handleCreateLocationTypeChange = (type: 'member' | 'business') => {
    setCreateLocationType(type);
    if (type === 'member') {
      setCreateData({
        ...createData,
        location_name: '',
        meeting_address: {},
      });
      setCreateSelectedCountry(null);
    } else {
      setCreateData({
        ...createData,
        location_user_id: '',
      });
    }
  };

  const handleEditLocationTypeChange = (type: 'member' | 'business') => {
    setEditLocationType(type);
    if (type === 'member') {
      setEditData({
        ...editData,
        location_name: '',
        meeting_address: {},
      });
      setEditSelectedCountry(null);
      setEditSelectedState(null);
    } else {
      setEditData({
        ...editData,
        location_user_id: '',
      });
    }
  };

  // Country/State change handlers for create form
  const handleCreateCountryChange = (country: any) => {
    setCreateSelectedCountry(country);
    setCreateData({
      ...createData,
      meeting_address: {
        ...createData.meeting_address,
        country_id: country?.id || '',
        state_id: '', // Reset state when country changes
      },
    });
  };

  const handleCreateStateChange = (state: any) => {
    setCreateData({
      ...createData,
      meeting_address: {
        ...createData.meeting_address,
        state_id: state?.id || '',
      },
    });
  };

  // Country/State change handlers for edit form
  const handleEditCountryChange = (country: any) => {
    setEditSelectedCountry(country);
    setEditSelectedState(null); // Reset state when country changes
    setEditData({
      ...editData,
      meeting_address: {
        ...editData.meeting_address,
        country_id: country?.id || '',
        state_id: '', // Reset state when country changes
      },
    });
  };

  const handleEditStateChange = (state: any) => {
    setEditSelectedState(state);
    setEditData({
      ...editData,
      meeting_address: {
        ...editData.meeting_address,
        state_id: state?.id || '',
      },
    });
  };

  // User selection handlers
  const handleLocationUserSelected = (user: any) => {
    if (isCreateModal) {
      setCreateData({
        ...createData,
        location_user_id: user.id,
      });
    } else {
      setEditData({
        ...editData,
        location_user_id: user.id,
      });
    }
    setShowLocationUserModal(false);
  };

  const handleHostUserSelected = (user: any) => {
    const newHost: HostRequest = {
      id: user.id,
      is_primary:
        selectedHostIndex === -1 &&
        ((isCreateModal &&
          (!createData.hosts || createData.hosts.length === 0)) ||
          (!isCreateModal && (!editData.hosts || editData.hosts.length === 0))),
    };

    const hostName = user.full_name || user.name || `User ${user.id}`;

    if (isCreateModal) {
      const updatedHosts = createData.hosts ? [...createData.hosts] : [];
      if (selectedHostIndex >= 0) {
        updatedHosts[selectedHostIndex] = newHost;
      } else {
        updatedHosts.push(newHost);
      }
      setCreateData({
        ...createData,
        hosts: updatedHosts,
      });
      // Update host names map
      const newNames = new Map(createHostNames);
      newNames.set(user.id, hostName);
      setCreateHostNames(newNames);
    } else {
      const updatedHosts = editData.hosts ? [...editData.hosts] : [];
      if (selectedHostIndex >= 0) {
        updatedHosts[selectedHostIndex] = newHost;
      } else {
        updatedHosts.push(newHost);
      }
      setEditData({
        ...editData,
        hosts: updatedHosts,
      });
      // Update host names map
      const newNames = new Map(editHostNames);
      newNames.set(user.id, hostName);
      setEditHostNames(newNames);
    }
    setSelectedHostIndex(-1);
    setShowHostUserModal(false);
  };

  // Host management
  const handleAddHost = (isEdit: boolean) => {
    setIsCreateModal(!isEdit);
    setSelectedHostIndex(-1);
    setShowHostUserModal(true);
  };

  const handleRemoveHost = (index: number, isEdit: boolean) => {
    if (isEdit) {
      const updatedHosts = editData.hosts ? [...editData.hosts] : [];
      const removedHost = updatedHosts[index];
      updatedHosts.splice(index, 1);
      setEditData({
        ...editData,
        hosts: updatedHosts,
      });
      // Remove from host names map
      if (removedHost) {
        const newNames = new Map(editHostNames);
        newNames.delete(removedHost.id);
        setEditHostNames(newNames);
      }
    } else {
      const updatedHosts = createData.hosts ? [...createData.hosts] : [];
      const removedHost = updatedHosts[index];
      updatedHosts.splice(index, 1);
      setCreateData({
        ...createData,
        hosts: updatedHosts,
      });
      // Remove from host names map
      if (removedHost) {
        const newNames = new Map(createHostNames);
        newNames.delete(removedHost.id);
        setCreateHostNames(newNames);
      }
    }
  };

  const handleSetPrimaryHost = (index: number, isEdit: boolean) => {
    if (isEdit) {
      const updatedHosts = editData.hosts
        ? editData.hosts.map((host, i) => ({
            ...host,
            is_primary: i === index,
          }))
        : [];
      setEditData({
        ...editData,
        hosts: updatedHosts,
      });
    } else {
      const updatedHosts = createData.hosts
        ? createData.hosts.map((host, i) => ({
            ...host,
            is_primary: i === index,
          }))
        : [];
      setCreateData({
        ...createData,
        hosts: updatedHosts,
      });
    }
  };

  // Helper function to convert empty strings to null
  const convertEmptyToNull = (value: any): any => {
    if (value === '') return null;
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const result: any = {};
      for (const key in value) {
        result[key] = convertEmptyToNull(value[key]);
      }
      return result;
    }
    if (Array.isArray(value)) {
      return value.map(convertEmptyToNull);
    }
    return value;
  };

  // Clean up data before saving based on location type
  const prepareEventData = (
    data: Partial<CreateEventRequest>,
    locationType: 'member' | 'business'
  ): Partial<CreateEventRequest> => {
    const cleaned: Partial<CreateEventRequest> = { ...data };

    if (locationType === 'member') {
      // Remove business location fields
      delete cleaned.location_name;
      delete cleaned.meeting_address;
      // Keep location_user_id if set, otherwise delete
      if (!cleaned.location_user_id) {
        delete cleaned.location_user_id;
      }
    } else {
      // Remove member location field
      delete cleaned.location_user_id;
      // Keep location_name and meeting_address if location_name is set
      if (!cleaned.location_name) {
        delete cleaned.location_name;
        delete cleaned.meeting_address;
      }
    }

    // Remove empty hosts array
    if (!cleaned.hosts || cleaned.hosts.length === 0) {
      delete cleaned.hosts;
    }

    // Convert empty strings to null
    return convertEmptyToNull(cleaned);
  };

  // Get unique event types from events
  const eventTypes = useMemo(() => {
    if (!eventsData) return [];
    const typesMap = new Map<string, string>();
    eventsData.forEach((event) => {
      if (!typesMap.has(event.event_type_id)) {
        typesMap.set(event.event_type_id, event.event_type_name);
      }
    });
    return Array.from(typesMap.entries()).map(([id, name]) => ({ id, name }));
  }, [eventsData]);

  const formatEventDate = (dateString: string): string => {
    if (!dateString) return '';
    // Parse UTC date and convert to local time for display
    return dayjs.utc(dateString).local().format('MMM D, YYYY h:mm A');
  };

  // Get event icon based on group type and event type (matching mobile app logic)
  const getEventIcon = (event: UpcomingEventResponse | EventDetailResponse) => {
    if (event.group_type_id || event.event_type_id === eventType.Social) {
      switch (event.group_type_id) {
        case groupTypes.Book:
          return BookGroupIcon;
        case groupTypes.Wine:
          return WineGroupIcon;
        case groupTypes.Exercis: // Fitness
          return WalkingRunningGroupIcon;
        case groupTypes.Dining:
          return DinnerGroupIcon;
        case groupTypes.Coffee:
          return CoffeeGroupIcon;
        case groupTypes.Crafts:
          return CraftsGroupIcon;
        case groupTypes.Game:
          return GameGroupIcon;
        case groupTypes.Committee:
          if (event.event_type_id === eventType.Meeting) {
            return DefaultIcon;
          } else {
            return SocialMeetingIconGold;
          }
        default:
          return SocialMeetingIconGold;
      }
    } else {
      return LocalChapterMeetingIcon;
    }
  };

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid container spacing={2} sx={{ p: 1 }}>
        <Grid item xs={12} sm={4}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(newValue) => newValue && setStartDate(newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(newValue) => newValue && setEndDate(newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          {canEditEvents && (
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={handleCreateEvent}
              disabled={isSaving}
              fullWidth
              size="small"
              sx={{ height: '40px' }}
            >
              Create Event
            </Button>
          )}
        </Grid>
      </Grid>

      {!filteredEvents || filteredEvents.length === 0 ? (
        <Grid item xs={12} textAlign={'center'} paddingTop={2}>
          <Typography variant="body1">
            No events found in the selected date range
          </Typography>
        </Grid>
      ) : (
        filteredEvents.map((event) => (
          <Grid item xs={12} md={6} sx={{ p: 1 }} key={event.id}>
            <Card
              sx={{
                p: 2,
                boxShadow: 3,
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                },
              }}
              onClick={() => handleEventClick(event.id)}
            >
              <CardHeader
                avatar={
                  <Avatar
                    src={getEventIcon(event)}
                    alt="Event icon"
                    sx={{ width: 40, height: 40 }}
                  />
                }
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">{event.title}</Typography>
                    {event.attending && (
                      <Chip label="Attending" color="success" size="small" />
                    )}
                  </Box>
                }
                subheader={
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {formatEventDate(event.meeting_on)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.event_type_name}
                      {event.group_name && ` • ${event.group_name}`}
                    </Typography>
                  </Box>
                }
              />
              {event.description && (
                <CardContent sx={{ p: 1, pt: 0 }}>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {event.description}
                  </Typography>
                </CardContent>
              )}
            </Card>
          </Grid>
        ))
      )}

      {/* Event Detail Modal */}
      <Dialog
        open={showDetailModal}
        onClose={handleCloseDetailModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {eventDetail && (
                <Avatar
                  src={getEventIcon(eventDetail)}
                  alt="Event icon"
                  sx={{ width: 48, height: 48 }}
                />
              )}
              <Typography variant="h6">
                {eventDetail?.title || 'Event Details'}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDetailModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {eventDetail ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {eventDetail.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {formatEventDate(eventDetail.meeting_on)}
                  {eventDetail.ending_on &&
                    ` - ${formatEventDate(eventDetail.ending_on)}`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
                  <Chip label={eventDetail.event_type_name} size="small" />
                  {eventDetail.group_name && (
                    <Chip
                      label={eventDetail.group_name}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {eventDetail.attending && (
                    <Chip label="Attending" color="success" size="small" />
                  )}
                </Box>
              </Grid>

              {eventDetail.description && (
                <Grid item xs={12}>
                  <Typography variant="body1">
                    {eventDetail.description}
                  </Typography>
                </Grid>
              )}

              {(eventDetail.meeting_link ||
                eventDetail.meeting_address ||
                eventDetail.location_name) && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Location
                  </Typography>
                  {eventDetail.meeting_link && (
                    <Typography variant="body2">
                      <a
                        href={eventDetail.meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {eventDetail.meeting_link}
                      </a>
                    </Typography>
                  )}
                  {eventDetail.location_name && (
                    <Typography variant="body2">
                      {eventDetail.location_name}
                    </Typography>
                  )}
                  {eventDetail.meeting_address && (
                    <Typography variant="body2">
                      {eventDetail.meeting_address.address_line_1}
                      {eventDetail.meeting_address.address_line_2 &&
                        `, ${eventDetail.meeting_address.address_line_2}`}
                      <br />
                      {eventDetail.meeting_address.city}
                      {eventDetail.meeting_address.state &&
                        `, ${eventDetail.meeting_address.state}`}
                      {eventDetail.meeting_address.postal_code &&
                        ` ${eventDetail.meeting_address.postal_code}`}
                    </Typography>
                  )}
                </Grid>
              )}

              {eventDetail.agenda && eventDetail.agenda.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Agenda
                  </Typography>
                  <List dense>
                    {eventDetail.agenda
                      .sort((a, b) => a.order - b.order)
                      .map((item) => (
                        <ListItem key={item.id}>
                          <ListItemText
                            primary={item.title}
                            secondary={item.description}
                          />
                        </ListItem>
                      ))}
                  </List>
                </Grid>
              )}

              {eventDetail.hosts && eventDetail.hosts.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Hosts
                  </Typography>
                  <Typography variant="body2">
                    {eventDetail.hosts
                      .map(
                        (host) =>
                          host.name + (host.is_primary ? ' (Primary)' : '')
                      )
                      .join(', ')}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  RSVPs: {eventDetail.rsvp_count}
                </Typography>
                {eventDetail.attendees && eventDetail.attendees.length > 0 && (
                  <List dense>
                    {eventDetail.attendees.map((attendee) => (
                      <ListItem key={attendee.id}>
                        <ListItemText
                          primary={attendee.name}
                          secondary={
                            <>
                              {attendee.is_guest && (
                                <Chip
                                  label="Guest"
                                  size="small"
                                  sx={{ mr: 1 }}
                                />
                              )}
                              {attendee.attending ? (
                                <Chip
                                  label="Attending"
                                  color="success"
                                  size="small"
                                />
                              ) : (
                                <Chip label="Not Attending" size="small" />
                              )}
                              {attendee.notes && (
                                <Typography variant="caption" display="block">
                                  {attendee.notes}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Grid>
            </Grid>
          ) : (
            <Typography>Loading event details...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {eventDetail?.can_edit && (
            <>
              <Button
                startIcon={<EditIcon />}
                onClick={handleEditEvent}
                color="primary"
              >
                Edit
              </Button>
              {isEventInFuture(eventDetail) && (
                <Button
                  startIcon={<DeleteIcon />}
                  onClick={() => setShowDeleteConfirm(true)}
                  color="error"
                >
                  Delete
                </Button>
              )}
            </>
          )}
          <Button onClick={handleCloseDetailModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Event Modal */}
      <Dialog
        open={showCreateModal}
        onClose={handleCloseCreateModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Event</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setCreateError(null)}
            >
              {createError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Event Title"
                value={createData.title}
                onChange={(e) =>
                  setCreateData({ ...createData, title: e.target.value })
                }
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={createData.description || ''}
                onChange={(e) =>
                  setCreateData({ ...createData, description: e.target.value })
                }
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Meeting Date/Time"
                value={
                  createData.meeting_on ? dayjs(createData.meeting_on) : null
                }
                onChange={(newValue) =>
                  setCreateData({
                    ...createData,
                    meeting_on: newValue
                      ? newValue.format('YYYY-MM-DD HH:mm:ss')
                      : '',
                  })
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    size: 'small',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Ending Date/Time"
                value={
                  createData.ending_on ? dayjs(createData.ending_on) : null
                }
                onChange={(newValue) => {
                  setCreateData({
                    ...createData,
                    ending_on: newValue
                      ? newValue.format('YYYY-MM-DD HH:mm:ss')
                      : '',
                  });
                  // Clear error when user changes the value
                  if (createError) setCreateError(null);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    size: 'small',
                    error:
                      createError !== null &&
                      (!createData.ending_on ||
                        dayjs(createData.ending_on).isBefore(
                          dayjs(createData.meeting_on)
                        )),
                    helperText:
                      createError &&
                      (!createData.ending_on ||
                        dayjs(createData.ending_on).isBefore(
                          dayjs(createData.meeting_on)
                        ))
                        ? createError
                        : '',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={createData.event_type_id || ''}
                  label="Event Type"
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      event_type_id: e.target.value,
                    })
                  }
                >
                  {eventTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Group (Optional)</InputLabel>
                <Select
                  value={createData.group_id || ''}
                  label="Group (Optional)"
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      group_id: e.target.value || '',
                    })
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {eventGroups?.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Meeting Link (Optional)"
                value={createData.meeting_link || ''}
                onChange={(e) =>
                  setCreateData({ ...createData, meeting_link: e.target.value })
                }
                fullWidth
              />
            </Grid>

            {/* Location Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <FormLabel component="legend">Location</FormLabel>
              <RadioGroup
                row
                value={createLocationType}
                onChange={(e) =>
                  handleCreateLocationTypeChange(
                    e.target.value as 'member' | 'business'
                  )
                }
              >
                <FormControlLabel
                  value="member"
                  control={<Radio />}
                  label="Member's House"
                />
                <FormControlLabel
                  value="business"
                  control={<Radio />}
                  label="Business Location"
                />
              </RadioGroup>
            </Grid>

            {createLocationType === 'member' && (
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsCreateModal(true);
                    setShowLocationUserModal(true);
                  }}
                  fullWidth
                >
                  {createData.location_user_id
                    ? 'Change Location Member'
                    : 'Select Location Member'}
                </Button>
              </Grid>
            )}

            {createLocationType === 'business' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    label="Location Name"
                    value={createData.location_name || ''}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        location_name: e.target.value,
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address Line 1"
                    value={createData.meeting_address?.address_line_1 || ''}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        meeting_address: {
                          ...createData.meeting_address,
                          address_line_1: e.target.value,
                        },
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address Line 2 (Optional)"
                    value={createData.meeting_address?.address_line_2 || ''}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        meeting_address: {
                          ...createData.meeting_address,
                          address_line_2: e.target.value,
                        },
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="City"
                    value={createData.meeting_address?.city || ''}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        meeting_address: {
                          ...createData.meeting_address,
                          city: e.target.value,
                        },
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CountrySelector
                    value={createData.meeting_address?.country_id || ''}
                    handleChange={handleCreateCountryChange}
                    required={false}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StateSelector
                    value={createData.meeting_address?.state_id || ''}
                    handleChange={handleCreateStateChange}
                    countryId={createData.meeting_address?.country_id}
                    required={false}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Postal Code"
                    value={createData.meeting_address?.postal_code || ''}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        meeting_address: {
                          ...createData.meeting_address,
                          postal_code: e.target.value,
                        },
                      })
                    }
                    fullWidth
                  />
                </Grid>
              </>
            )}

            {/* Hosts Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <FormLabel component="legend">Hosts (Optional)</FormLabel>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddHost(false)}
                >
                  Add Host
                </Button>
              </Box>
              {createData.hosts && createData.hosts.length > 0 && (
                <List dense>
                  {createData.hosts.map((host, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveHost(index, false)}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          createHostNames.get(host.id) || `Host ${index + 1}`
                        }
                        secondary={
                          <Box>
                            <Chip
                              label={
                                host.is_primary ? 'Primary' : 'Not Primary'
                              }
                              size="small"
                              color={host.is_primary ? 'primary' : 'default'}
                              onClick={() => handleSetPrimaryHost(index, false)}
                              sx={{ cursor: 'pointer', mr: 1 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateModal} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEvent}
            variant="contained"
            color="success"
            disabled={
              isSaving ||
              !createData.title ||
              !createData.meeting_on ||
              !createData.ending_on ||
              !createData.event_type_id
            }
          >
            Create Event
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Event Modal */}
      <Dialog
        open={showEditModal}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setEditError(null)}
            >
              {editError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Event Title"
                value={editData.title || ''}
                onChange={(e) =>
                  setEditData({ ...editData, title: e.target.value })
                }
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={editData.description || ''}
                onChange={(e) =>
                  setEditData({ ...editData, description: e.target.value })
                }
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Meeting Date/Time"
                value={editData.meeting_on ? dayjs(editData.meeting_on) : null}
                onChange={(newValue) =>
                  setEditData({
                    ...editData,
                    meeting_on: newValue
                      ? newValue.format('YYYY-MM-DD HH:mm:ss')
                      : '',
                  })
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    size: 'small',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Ending Date/Time"
                value={editData.ending_on ? dayjs(editData.ending_on) : null}
                onChange={(newValue) => {
                  setEditData({
                    ...editData,
                    ending_on: newValue
                      ? newValue.format('YYYY-MM-DD HH:mm:ss')
                      : '',
                  });
                  // Clear error when user changes the value
                  if (editError) setEditError(null);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    size: 'small',
                    error:
                      editError !== null &&
                      (!editData.ending_on ||
                        dayjs(editData.ending_on).isBefore(
                          dayjs(editData.meeting_on)
                        )),
                    helperText:
                      editError &&
                      (!editData.ending_on ||
                        dayjs(editData.ending_on).isBefore(
                          dayjs(editData.meeting_on)
                        ))
                        ? editError
                        : '',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={editData.event_type_id || ''}
                  label="Event Type"
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      event_type_id: e.target.value,
                    })
                  }
                >
                  {eventTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Group (Optional)</InputLabel>
                <Select
                  value={editData.group_id || ''}
                  label="Group (Optional)"
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      group_id: e.target.value || '',
                    })
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {eventGroups?.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Meeting Link (Optional)"
                value={editData.meeting_link || ''}
                onChange={(e) =>
                  setEditData({ ...editData, meeting_link: e.target.value })
                }
                fullWidth
              />
            </Grid>

            {/* Location Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <FormLabel component="legend">Location</FormLabel>
              <RadioGroup
                row
                value={editLocationType}
                onChange={(e) =>
                  handleEditLocationTypeChange(
                    e.target.value as 'member' | 'business'
                  )
                }
              >
                <FormControlLabel
                  value="member"
                  control={<Radio />}
                  label="Member's House"
                />
                <FormControlLabel
                  value="business"
                  control={<Radio />}
                  label="Business Location"
                />
              </RadioGroup>
            </Grid>

            {editLocationType === 'member' && (
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsCreateModal(false);
                    setShowLocationUserModal(true);
                  }}
                  fullWidth
                >
                  {editData.location_user_id
                    ? 'Change Location Member'
                    : 'Select Location Member'}
                </Button>
              </Grid>
            )}

            {editLocationType === 'business' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    label="Location Name"
                    value={editData.location_name || ''}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        location_name: e.target.value,
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address Line 1"
                    value={editData.meeting_address?.address_line_1 || ''}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        meeting_address: {
                          ...editData.meeting_address,
                          address_line_1: e.target.value,
                        },
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address Line 2 (Optional)"
                    value={editData.meeting_address?.address_line_2 || ''}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        meeting_address: {
                          ...editData.meeting_address,
                          address_line_2: e.target.value,
                        },
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="City"
                    value={editData.meeting_address?.city || ''}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        meeting_address: {
                          ...editData.meeting_address,
                          city: e.target.value,
                        },
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CountrySelector
                    value={editData.meeting_address?.country_id || ''}
                    handleChange={handleEditCountryChange}
                    required={false}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StateSelector
                    value={editData.meeting_address?.state_id || ''}
                    handleChange={handleEditStateChange}
                    countryId={editData.meeting_address?.country_id}
                    required={false}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Postal Code"
                    value={editData.meeting_address?.postal_code || ''}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        meeting_address: {
                          ...editData.meeting_address,
                          postal_code: e.target.value,
                        },
                      })
                    }
                    fullWidth
                  />
                </Grid>
              </>
            )}

            {/* Hosts Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <FormLabel component="legend">Hosts (Optional)</FormLabel>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddHost(true)}
                >
                  Add Host
                </Button>
              </Box>
              {editData.hosts && editData.hosts.length > 0 && (
                <List dense>
                  {editData.hosts.map((host, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveHost(index, true)}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          editHostNames.get(host.id) || `Host ${index + 1}`
                        }
                        secondary={
                          <Box>
                            <Chip
                              label={
                                host.is_primary ? 'Primary' : 'Not Primary'
                              }
                              size="small"
                              color={host.is_primary ? 'primary' : 'default'}
                              onClick={() => handleSetPrimaryHost(index, true)}
                              sx={{ cursor: 'pointer', mr: 1 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEditEvent}
            variant="contained"
            color="primary"
            disabled={
              isSaving ||
              !editData.title ||
              !editData.meeting_on ||
              !editData.ending_on ||
              !editData.event_type_id
            }
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        maxWidth="sm"
      >
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{eventDetail?.title}"? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteEvent}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Search Modals */}
      <UserSearchModal
        modalOpen={showLocationUserModal}
        setUser={handleLocationUserSelected}
        setModalOpen={setShowLocationUserModal}
        chapter={id}
        spd={undefined}
        userObjectRet={true}
        statusFilter="active"
      />
      <UserSearchModal
        modalOpen={showHostUserModal}
        setUser={handleHostUserSelected}
        setModalOpen={setShowHostUserModal}
        chapter={id}
        spd={undefined}
        userObjectRet={true}
        statusFilter="active"
      />
    </Grid>
  );
};

export default LCEvents;
